/**
 * Tag System Types
 * Types for the default tag system (Interest, Game Impact, Body Type, etc.)
 */

export interface DefaultTagDefinition {
  tagName: string;
  tagType: 'single-select' | 'multi-select';
  displayOrder: number;
  options: string[];
  /** True if this is one of the 6 built-in default categories */
  isDefault?: boolean;
  /** True if this was created by the organization */
  isCustom?: boolean;
}

export interface UpdateProspectDefaultTagsRequest {
  tagName: string;
  values: string[];
}

export interface CreateTagDefinitionRequest {
  tagName: string;
  tagType: 'single-select' | 'multi-select';
  options: string[];
}

/**
 * Reserved filter key for the built-in Grad Year filter. This is NOT a tag
 * category — selections under this key are sent to the API as `gradYear` query
 * params and matched against the scalar Prospect.gradYear field, not the tag
 * relation. Kept in the TagFilters map so it shares the existing filter chip,
 * count, and URL-serialization plumbing.
 */
export const GRAD_YEAR_FILTER_KEY = "Grad Year";

/**
 * Tag Filter State
 * Keyed by tag name (supports both default and custom categories).
 * Empty/unselected tags are omitted entirely (not stored as empty arrays).
 */
export type TagFilters = Record<string, string[]>;

/**
 * Reserved filter key for the built-in Level filter (High School / JUCO /
 * Transfer Portal). Like Grad Year this is NOT a tag category — selections are
 * sent to the API as repeated `playerLevel` query params and matched against the
 * scalar `Prospect.playerLevel` column.
 *
 * Values are the **wire ordinals as strings** (`'0' | '1' | '2'`), because
 * `TagFilters` is `Record<string, string[]>`. Ordinal `0` is High School, so a
 * value here is never tested for truthiness.
 */
export const PLAYER_LEVEL_FILTER_KEY = 'Level';

/**
 * Reserved filter key for the built-in JC Year filter. Sent as repeated
 * `jucoYear` params; values are `'1' | '2' | '3'`, matched against the scalar
 * `Prospect.jucoYear` column.
 *
 * Only meaningful alongside `Level = JUCO`: the server ORs within a facet and
 * ANDs across facets, so `Level=[HighSchool] + JucoYear=['1']` matches nobody.
 * See {@link isJucoYearFacetEnabled}.
 */
export const JUCO_YEAR_FILTER_KEY = 'JC Year';

/**
 * The Level facet's value that means JUCO — the wire ordinal as a string.
 *
 * Held as a literal rather than imported from the player-level constants so that
 * this module stays identical across web and mobile, which resolve those
 * constants from different paths. Each platform's test asserts it against that
 * platform's shared JUCO ordinal, so the literal cannot drift.
 */
export const JUCO_LEVEL_FILTER_VALUE = '1';

/**
 * Every key that is a dedicated scalar facet rather than a tag category.
 *
 * A tag definition sharing one of these names must not be rendered as its own
 * section: both sections would write to the same `TagFilters` key, and the
 * reserved branch in `appendTagFilterParams` would then send the org tag's
 * option strings as `playerLevel` / `jucoYear` / `gradYear` — which the API
 * rejects with a 400 by design. This is the same collision
 * `playersTableColumns.ts` already special-cases for the org "Class" tag.
 */
export const RESERVED_FILTER_KEYS: readonly string[] = [
  GRAD_YEAR_FILTER_KEY,
  PLAYER_LEVEL_FILTER_KEY,
  JUCO_YEAR_FILTER_KEY,
];

/** True when this filter key is a built-in scalar facet, not an org tag category. */
export const isReservedFilterKey = (key: string): boolean =>
  RESERVED_FILTER_KEYS.includes(key);

/**
 * Whether the JC Year facet is offerable at all: only when Level includes JUCO.
 *
 * Not a styling detail — a JC Year selected without JUCO ANDs down to an empty
 * result set, so the control would look active while guaranteeing zero rows.
 */
export const isJucoYearFacetEnabled = (filters: TagFilters): boolean =>
  (filters[PLAYER_LEVEL_FILTER_KEY] ?? []).includes(JUCO_LEVEL_FILTER_VALUE);

/**
 * Drop any JC Year selection that Level no longer supports.
 *
 * Called whenever Level changes and again before applying, so a stale JC Year
 * can never ride along and silently empty the result set.
 */
export const pruneJucoYearFacet = (filters: TagFilters): TagFilters => {
  if (isJucoYearFacetEnabled(filters)) return filters;
  if (!filters[JUCO_YEAR_FILTER_KEY]) return filters;
  const pruned: TagFilters = {};
  for (const [key, values] of Object.entries(filters)) {
    if (key !== JUCO_YEAR_FILTER_KEY) pruned[key] = values;
  }
  return pruned;
};


/**
 * Count total active filter values across all tag categories
 */
export const countActiveFilters = (filters: TagFilters): number => {
  return Object.values(filters).reduce(
    (sum, values) => sum + (values?.length ?? 0),
    0
  );
};

/**
 * Check if any filters are active
 */
export const hasActiveFilters = (filters: TagFilters): boolean => {
  return Object.values(filters).some(values => values && values.length > 0);
};
